import type { PropsWithChildren, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Editorial-style section: підпис uppercase + округла warm-card.
 * Стиль 1:1 з handoff'а: white card з shadow-card, кутовий радіус 22.
 */
interface SectionProps extends PropsWithChildren {
  title?: string;
  footer?: ReactNode;
  className?: string;
  /** Класи самої картки. `p-0` — для списку з власними відступами рядків
   *  (наприклад, «Топ перемог місяця»), щоб аватарки стояли на тій самій
   *  вертикалі, що й плитки сусідніх карток із `p-4`. */
  bodyClassName?: string;
}

export function Section({ title, footer, children, className, bodyClassName }: SectionProps) {
  return (
    <div className={cn("mb-6", className)}>
      {title && (
        <h2 className="mb-2.5 font-display text-[0.75rem] font-semibold uppercase tracking-[0.06em] text-ink-dim">
          {title}
        </h2>
      )}
      <div
        className={cn(
          "overflow-hidden rounded-ios-card bg-surface px-1.5 py-1 shadow-card-soft",
          bodyClassName,
        )}
      >
        {children}
      </div>
      {footer && (
        <div className="pt-2.5 text-[0.6875rem] leading-relaxed text-ink-dim">
          {footer}
        </div>
      )}
    </div>
  );
}

interface SectionRowProps extends PropsWithChildren {
  onClick?: () => void;
  leading?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}

export function SectionRow({
  children,
  onClick,
  leading,
  trailing,
  className,
}: SectionRowProps) {
  const interactive = !!onClick;
  const baseClass = cn(
    "flex w-full items-center gap-3 border-b border-line px-4 py-3.5 text-left text-ink last:border-b-0",
    interactive && "active:bg-surface-soft cursor-pointer",
    className,
  );

  const inner = (
    <>
      {leading && <div className="shrink-0">{leading}</div>}
      <div className="min-w-0 flex-1">{children}</div>
      {trailing !== undefined ? (
        <div className="shrink-0 text-ink-dim">{trailing}</div>
      ) : interactive ? (
        <span className="shrink-0 text-ink-faint">›</span>
      ) : null}
    </>
  );

  // Інтерактивний рядок — <button>. Не-інтерактивний — <div>,
  // інакше disabled-button блокує pointer-events у вкладених
  // кнопках (звёзди оцінки уроку, наприклад).
  if (interactive) {
    return (
      <button type="button" onClick={onClick} className={baseClass}>
        {inner}
      </button>
    );
  }
  return <div className={baseClass}>{inner}</div>;
}
